import { getDefaultStore, useAtomValue } from "jotai";
import { Fragment, type PropsWithChildren, type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import {
    OnMouseOver,
    specialHoveredAtom,
    specialsAtom,
    useSpecialSupportSection,
    writeAtom,
} from "./constants";

const store = getDefaultStore();

const HIDE_DELAY_MS = 200;

type SpecialSupportProps = PropsWithChildren<{
    content: ReactNode;
    enabled: boolean | typeof OnMouseOver;
}>;

export default function SpecialSupport(props: SpecialSupportProps) {
    const { content, enabled, children } = props;
    const section = useSpecialSupportSection();
    const element = useAtomValue(section.element);
    const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
        store.set(specialsAtom, (c) => [...c, section]);
        return () => {
            store.set(specialsAtom, (c) => c.filter((s) => s.id !== section.id));
            store.set(specialHoveredAtom, (v) => (v === section ? null : v));
        };
    }, [section]);

    const update = () => {
        if (!anchor) return;
        const childrenRect = anchor.getBoundingClientRect();
        writeAtom(section.offset, childrenRect.top);
        writeAtom(section.height, childrenRect.height);
        const visible = enabled === OnMouseOver ? hovered : enabled;
        writeAtom(section.visible, visible);
    };

    useEffect(() => {
        update();
    }, [enabled, hovered]);

    useEffect(() => {
        update();
        if (!anchor) return;
        const observer = new ResizeObserver(update);
        observer.observe(anchor);
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            observer.disconnect();
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [anchor, section]);

    useEffect(() => {
        if (enabled !== OnMouseOver) return;
        if (!anchor) return;
        const onMouseEnter = () => setHovered(true);
        const onMouseLeave = () => setTimeout(() => setHovered(false), HIDE_DELAY_MS);
        anchor.addEventListener("mouseenter", onMouseEnter);
        anchor.addEventListener("mouseleave", onMouseLeave);
        return () => {
            anchor.removeEventListener("mouseenter", onMouseEnter);
            anchor.removeEventListener("mouseleave", onMouseLeave);
        };
    }, [enabled, anchor]);

    return (
        <Fragment>
            {element ? createPortal(content, element) : null}
            <div ref={setAnchor}>{children}</div>
        </Fragment>
    );
}
